import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import TextField from '@mui/material/TextField';
import { useEffect, type ReactElement } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

/**
 * Mark-manually-settled dialog (AUT-128, architect guardrail #6).
 *
 * A required `note` (1-500 chars, mirrors backend `MarkPayoutManualSchema`)
 * collected via RHF + Zod — the note threads to `Payout.manualSettlementNote`
 * AND the audit row's `reason`. Confirm is blocked until the note is valid,
 * so the parent never POSTs an empty note. On confirm the parent POSTs
 * `{note}` (role + permission + audit gated server-side).
 *
 * Backend remains the 409 authority on the FSM transition — this dialog is
 * collecting the justification.
 */
const MAX_NOTE = 500;

const schema = z.object({
  note: z
    .string()
    .trim()
    .min(1, 'payouts.markManualDialog.noteRequired')
    .max(MAX_NOTE, 'payouts.markManualDialog.noteMax'),
});

type MarkManualForm = z.infer<typeof schema>;

export interface MarkManualDialogProps {
  open: boolean;
  loading?: boolean;
  onConfirm: (note: string) => void;
  onCancel: () => void;
}

export const MarkManualDialog = ({
  open,
  loading = false,
  onConfirm,
  onCancel,
}: MarkManualDialogProps): ReactElement => {
  const { t } = useTranslation();
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isValid },
  } = useForm<MarkManualForm>({
    resolver: zodResolver(schema),
    mode: 'onChange',
    defaultValues: { note: '' },
  });

  useEffect(() => {
    if (open) reset({ note: '' });
  }, [open, reset]);

  const submit = ({ note }: MarkManualForm): void => {
    if (loading) return;
    onConfirm(note.trim());
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onCancel} maxWidth="xs" fullWidth>
      <DialogTitle>{t('payouts.markManualDialog.title')}</DialogTitle>
      <form onSubmit={handleSubmit(submit)}>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <DialogContentText>{t('payouts.markManualDialog.description')}</DialogContentText>
          <TextField
            {...register('note')}
            fullWidth
            multiline
            minRows={3}
            size="small"
            disabled={loading}
            label={t('payouts.markManualDialog.noteLabel')}
            error={Boolean(errors.note)}
            helperText={errors.note ? t(errors.note.message ?? '') : ' '}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading || !isValid}
            loading={loading}
          >
            {t('payouts.markManualDialog.confirm')}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};
